import { tr } from '../translation';
import process from 'node:process';

/**
 * This method to get the error message from error description response
 * @param {string|string[]} error
 * @param {string} fallBackMessage
 *
 * @return {string}
 */
export const getErrorMessage = (error: string | string[], fallBackMessage: string = tr('app.error')): string => {
  if (!error) {
    return fallBackMessage;
  }
  if (typeof error === 'string') {
    if (error.includes('undefined')) {
      return fallBackMessage;
    }
    return error;
  }
  if (error.length) {
    return getErrorMessage(error[0]);
  }
  return fallBackMessage;
};

/**
 * Splits the input text into an array containing the parts of the text and any URLs found within it.
 * URLs are identified based on a predefined pattern that matches most common URL formats, including
 * protocols like http, https, ftp, and file.
 *
 * @param {string} text - The input string that may contain one or more URLs.
 * @returns {string[]} An array of strings, where each element is either a part of the original text
 *                      without URLs or an entire URL found in the text. If the input text is empty
 *                      or does not contain any URLs, the function returns an array containing
 *                      the original text or an empty array if the text is empty.
 */
export const getUrlParts = (text: string): string[] => {
  if (!text) {
    return [];
  }
  if (typeof text !== 'string') {
    text = String(text); // Ensure the input is a string
  }
  const urlPattern =
    /(\b(?:https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:.;,]*[-A-Z0-9+&@#/%=~_|]\b)|(\bwww\.[-A-Z0-9+&@#/%?=~_|!:.;,]*[-A-Z0-9+&@#/%=~_|]\b)/gi;
  return text.split(urlPattern);
};
/**
 * Determines whether the input text is a URL.
 * This function utilizes a regular expression pattern to identify URLs based on common URL formats,
 * including protocols such as http, https, ftp, and file. It is useful for validating whether a given
 * string of text represents a valid URL.
 *
 * @param {string} text - The text string to be validated as a URL.
 * @returns {boolean} Returns true if the input text matches the URL pattern, indicating it is a URL; otherwise, returns false.
 */
export const isUrl = (text: string): boolean => {
  let localText = text || '';
  if (!localText) {
    return false;
  }
  if (typeof localText !== 'string') {
    localText = String(localText); // Ensure the input is a string
  }
  const urlPattern =
    /(\b(?:https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:.;,]*[-A-Z0-9+&@#/%=~_|]\b)|(\bwww\.[-A-Z0-9+&@#/%?=~_|!:.;,]*[-A-Z0-9+&@#/%=~_|]\b)/gi;

  return !!localText.match(urlPattern);
};

/**
 * Opening the link with some app, if the URL scheme is "http" the web link should be opened
 * by the browser in the web environment
 * @param url
 */
export const openUrl = (url) => {
  try {
    // Add 'https://' if URL starts with 'www.'
    let tempUrl = url;
    if (url.startsWith('www.')) {
      tempUrl = `https://${tempUrl}`;
    }

    // Open the URL in a new browser tab
    window.open(tempUrl, '_blank', 'noopener,noreferrer');
  } catch (e) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.error(e);
    }
  }
};

/**
 * Removes all HTML tags from the given string.
 *
 * @param {string} text - The input string that may contain HTML tags.
 * @returns {string} - The cleaned text without any HTML tags.
 */
export const removeHtmlTags = (text: string): string => {
  if (!text) return '';
  return text.replace(/<[^>]*>/g, ''); // Regular expression to match HTML tags
};

export const generateUniqueId = (prefix = '') => {
  return `${prefix}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const getMaleVoice = (voices: any) => {
  // Get all available voices
  if (!(voices && Array.isArray(voices))) {
    return undefined;
  }
  // Filter to only English voices
  const englishVoices = (voices || []).filter((voice) => voice.lang.toLowerCase().startsWith('en'));

  // Priority order of male voices
  const priorityNames = ['David', 'Mark', 'Alex', 'Aaron', 'Fred'];

  // Search in order; return on the first match
  for (const name of priorityNames) {
    const matchedVoice = englishVoices.find((voice) => voice.name.toLowerCase().includes(name.toLowerCase()));
    if (matchedVoice) {
      return matchedVoice;
    }
  }

  // Fallback: return the first English voice or null if none
  return englishVoices[0] || undefined;
};
